import { Controller } from "@hotwired/stimulus"
import { logEvent } from "../field_logger"
import { batteryMonitoringEnabled } from "../battery_monitor"
import { foregroundTimeStamp } from "../field_foreground_time"

// Battery percentage prompt for iPhone Taskers (card #4109).
//
// Safari on iPhone has no Battery Status API, so field-battery cannot read the
// battery there. Every 30 minutes, while the app is open and in the foreground,
// this asks the Tasker to type the percentage in. The answer is written to the
// diagnostics log in the same category and shape as a real reading, plus
// source: "manual" so reports can tell the two apart.
//
// Only runs on a device with no navigator.getBattery AND an iPhone user agent,
// so it never doubles up with the real monitor. It follows the Tasker's own
// battery setting (card #4153): off by default on iPhone, on once the Tasker
// switches it on in Settings or head office switches it on for them. The
// partial is always rendered; field:battery-monitoring-changed (the Settings
// toggle, or a settings pull) starts or stops it straight away.
//
// Quiet by design:
//   - checks only while the page is visible (iOS suspends timers when hidden, so
//     the elapsed time is compared against a stored timestamp, not counted)
//   - never shows over the camera or scanner sheet, an open dialog, or a text
//     field the Tasker is typing in; it waits for the next calm moment
//   - swipe it sideways, or tap Not now: that is "skipped", logged once, and the
//     30 minute clock restarts. Saving restarts it too.
//   - the time it was last shown is stored in localStorage, so reopening the
//     app does not prompt again straight away
//
// Dev shortcut (not a user feature): in the browser console run
//   localStorage.setItem("field:battery_prompt_interval_ms", "10000")
// to prompt after 10 seconds instead of 30 minutes. Remove the key to go back.
//
// Targets: form, input, lowPower, error

export const BATTERY_PROMPT_INTERVAL_MS = 30 * 60 * 1000
export const BATTERY_PROMPT_LAST_KEY = "field:battery_prompt_last_at"
export const BATTERY_PROMPT_DEV_INTERVAL_KEY = "field:battery_prompt_interval_ms"

const CHECK_EVERY_MS = 15_000
const SWIPE_DISTANCE_PX = 60
const SHEET_OVERLAY_SELECTOR =
  '[data-field-photo-camera-target="overlay"], [data-field-barcode-scanner-target="overlay"]'

// Page JS outlives Turbo visits, so these survive a navigation that replaces
// the body (and this controller) while the prompt is up or storage is blocked.
let pendingShown = false
let lastPromptedAtFallback = null

export default class extends Controller {
  static targets = ["form", "input", "lowPower", "error"]

  connect() {
    this._visibilityHandler = () => this._onVisibilityChange()
    this._settingChangedHandler = () => this._apply(true)
    document.addEventListener("visibilitychange", this._visibilityHandler)
    window.addEventListener("field:battery-monitoring-changed", this._settingChangedHandler)

    this._active = false
    this._apply(false)
  }

  disconnect() {
    this._stopChecking()
    if (this._visibilityHandler) document.removeEventListener("visibilitychange", this._visibilityHandler)
    if (this._settingChangedHandler) window.removeEventListener("field:battery-monitoring-changed", this._settingChangedHandler)
  }

  submit(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    const level = Number(text)
    if (text === "" || !Number.isInteger(level) || level < 0 || level > 100) {
      this.errorTarget.classList.remove("hidden")
      this.inputTarget.focus()
      return
    }

    // charging is unknown from a typed answer; null keeps the field name of a real reading.
    logEvent("battery", "Battery reading", {
      level,
      charging: null,
      lowPowerMode: this.lowPowerTarget.checked,
      source: "manual",
      ...foregroundTimeStamp()
    }).catch(() => {})
    this._finish()
  }

  skip() {
    logEvent("battery", "Battery prompt dismissed", { source: "manual" }).catch(() => {})
    this._finish()
  }

  touchStart(event) {
    this._touchStartX = event.changedTouches[0].clientX
  }

  touchEnd(event) {
    if (this._touchStartX === undefined) return
    const distance = Math.abs(event.changedTouches[0].clientX - this._touchStartX)
    this._touchStartX = undefined
    if (distance >= SWIPE_DISTANCE_PX) this.skip()
  }

  // -- private --

  _supported() {
    return typeof navigator.getBattery !== "function" && /iPhone/.test(navigator.userAgent)
  }

  // Starts or stops the prompt to match the Tasker's setting. Switching it on
  // restarts the 30 minute clock, so the first prompt is half an hour away and
  // not straight after an old timestamp. Switching it off closes an open prompt
  // without logging a dismissal.
  _apply(changed) {
    const shouldRun = this._supported() && batteryMonitoringEnabled()

    if (shouldRun) {
      if (this._lastPromptedAt() === null || (changed && !this._active)) this._storeLastPromptedAt(Date.now())
      this._active = true
      if (pendingShown) this._show()
      if (document.visibilityState === "visible") this._startChecking()
    } else {
      this._active = false
      this._stopChecking()
      pendingShown = false
      this.element.classList.add("hidden")
    }
  }

  _onVisibilityChange() {
    if (!this._active) return

    if (document.visibilityState === "visible") {
      this._startChecking()
    } else {
      this._stopChecking()
    }
  }

  _startChecking() {
    this._stopChecking()
    this._check()
    this._timer = setInterval(() => this._check(), CHECK_EVERY_MS)
  }

  _stopChecking() {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
  }

  _check() {
    if (document.visibilityState !== "visible") return
    if (this._shown()) return
    if (Date.now() - this._lastPromptedAt() < this._intervalMs()) return
    if (!this._calm()) return

    this._storeLastPromptedAt(Date.now())
    this._show()
  }

  // Nothing in progress that a prompt could get in the way of.
  _calm() {
    if (document.querySelector("dialog[open]")) return false

    const sheetOpen = Array.from(document.querySelectorAll(SHEET_OVERLAY_SELECTOR))
      .some((overlay) => overlay.style.pointerEvents === "auto")
    if (sheetOpen) return false

    const active = document.activeElement
    const typing = active && active !== document.body &&
      active.matches("input, textarea, select, [contenteditable='true']")
    return !typing
  }

  _shown() {
    return !this.element.classList.contains("hidden")
  }

  _show() {
    pendingShown = true
    this.formTarget.reset()
    this.errorTarget.classList.add("hidden")
    this.element.classList.remove("hidden")
  }

  _finish() {
    pendingShown = false
    this._storeLastPromptedAt(Date.now())
    this.element.classList.add("hidden")
    if (document.activeElement) document.activeElement.blur()
  }

  _intervalMs() {
    const override = Number(this._read(BATTERY_PROMPT_DEV_INTERVAL_KEY))
    return override > 0 ? override : BATTERY_PROMPT_INTERVAL_MS
  }

  _lastPromptedAt() {
    const stored = Number(this._read(BATTERY_PROMPT_LAST_KEY))
    if (stored > 0) return stored
    return lastPromptedAtFallback
  }

  _storeLastPromptedAt(timestamp) {
    lastPromptedAtFallback = timestamp
    try {
      localStorage.setItem(BATTERY_PROMPT_LAST_KEY, String(timestamp))
    } catch {
      // storage blocked: the module value above still stops a repeat this session
    }
  }

  _read(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }
}
